import { Box, Checkbox, Divider, Typography } from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import { useBoundStore } from "useStore";
import { formatearNumeroConComas } from "views/Components/Shared/utils/formatNumbers";
import { CmSendStatus } from "./CmSendStatus";

const COL_WIDTH = {
  checkbox: 80,
  name: 300,
  budget: 350,
  roas: 300,
  send: 220,
};

function ValueChange({
  label,
  original,
  edited,
  defaultValue,
  prefix,
  suffix,
}) {
  const changed =
    original !== undefined &&
    edited !== undefined &&
    Number(edited) !== Number(original);

  const fmt = (v) =>
    v != null
      ? `${prefix || ""}${formatearNumeroConComas(v)}${suffix || ""}`
      : "-";

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <Typography sx={{ color: "#00000099" }} fontWeight={400} fontSize="14px">
        {label}
      </Typography>
      {changed ? (
        <>
          <Typography
            sx={{ color: "#000000DE", fontWeight: 400, fontSize: "14px" }}
          >
            {fmt(original)}
          </Typography>
          <ArrowForwardOutlinedIcon fontSize="small" />
          <Typography fontWeight={700} fontSize="14px" color="#000000DE">
            {fmt(edited)}
          </Typography>
        </>
      ) : (
        <Typography
          sx={{ color: "#000000DE", fontWeight: 400, fontSize: "14px" }}
        >
          {fmt(defaultValue)}
        </Typography>
      )}
    </Box>
  );
}

function CmCampaignRow({
  rowId,
  row,
  isSelected,
  handleRowSelectionChange,
  loading,
  sendState,
  feedbackByRow,
}) {
  const budgetField = row?.dailyBudget;
  const roasField = row?.targetRoas;
  const isDisabled = loading || sendState === "success";
  const budgetFieldDefaultValue = row?.dailyBudget;
  const roasFieldDefaultValue = row?.targetRoas;

  const feedback = feedbackByRow[rowId];
  const displayState = (() => {
    if (feedback && !feedback.success) return "error";
    if (feedback?.success === true) return "success";
    return "pending";
  })();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "#EEF1F6",
        borderLeft: "4px solid #002878",
        borderRadius: 1,
        py: 1,
        mb: 1,
      }}
    >
      {/* checkbox */}
      <Box
        sx={{
          width: COL_WIDTH.checkbox,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Checkbox
          checked={isSelected}
          onChange={() => handleRowSelectionChange(rowId)}
          disabled={isDisabled}
          sx={{
            p: 0,
            "&.Mui-checked": { color: "#002878" },
          }}
        />
      </Box>

      {/* campaign name */}
      <Box sx={{ width: COL_WIDTH.name }}>
        <Typography
          fontWeight={600}
          fontSize="16px"
          sx={{ color: "#000000DE" }}
        >
          {row?.campaignName || rowId}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={400}
          fontSize="14px"
          sx={{ color: "#00000099" }}
        >
          {row.adCount} {row.adCount === 1 ? "anuncio" : "anuncios"}
        </Typography>
      </Box>

      {/* budget */}
      <Box sx={{ width: COL_WIDTH.budget }}>
        <ValueChange
          label="Presupuesto Diario:"
          original={budgetField?.valueOriginal}
          edited={budgetField?.valueEdited}
          defaultValue={budgetFieldDefaultValue}
          prefix="$"
        />
      </Box>

      {/* roas */}
      <Box sx={{ width: COL_WIDTH.roas }}>
        <ValueChange
          label="ROAS Objetivo:"
          original={roasField?.valueOriginal}
          edited={roasField?.valueEdited}
          defaultValue={roasFieldDefaultValue}
          suffix="x"
        />
      </Box>

      {/* send status */}
      <Box
        sx={{
          width: COL_WIDTH.send,
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        {isSelected && (
          <CmSendStatus
            send={displayState}
            loading={loading}
            message={feedback?.message}
            isChecked={isSelected}
          />
        )}
      </Box>
    </Box>
  );
}

export function CmPublishTable({
  allModifiedRowKeys,
  selectedRows,
  handleRowSelectionChange,
  loading,
  sendState,
  feedbackByRow,
}) {
  const cmEditedRows = useBoundStore((state) => state.cmEditedRows);
  const isDisabled = loading || sendState === "success";

  return (
    <>
      {allModifiedRowKeys.map((rowId) => {
        const row = cmEditedRows[rowId];
        if (!row) return null;

        return (
          <CmCampaignRow
            key={rowId}
            rowId={rowId}
            row={row}
            isSelected={selectedRows[rowId] === true}
            handleRowSelectionChange={handleRowSelectionChange}
            loading={loading}
            sendState={sendState}
            feedbackByRow={feedbackByRow}
          />
        );
      })}
    </>
  );
}
